import "../../scss/styles.scss";
import { renderHeader } from "../ui/header";
import { renderFooter } from "../ui/footer";
import { initFirebase, fetchPublicConfig, listProducts } from "../services/firebaseClient";
import { isOnline, onOffline, onOnline } from "../services/network";
import { productosMock } from "../data/productos.mock";
import { filterProducts, getCategories, sortProducts } from "../catalogo/filtros";
import { getPriceForPresentation, renderCatalog } from "../catalogo/render";
import { openProductModal } from "../catalogo/modal";
import { getCatalog, refreshCatalog, writeCache } from "../services/catalogCache";
import {
  addItem,
  getCart,
  getItemCount,
  getTotal,
  initCart,
  removeItem,
  setQuantity,
} from "../cart/cartStore";
import { createCartUI } from "../cart/cartUI";
import { createScheduleComponent } from "../services/scheduleWidget";
import { getSchedule, isOpenNow } from "../services/scheduleService";
import { trackPageView } from "../services/analytics";
import { mountPublicAuth } from "../ui/publicAuthUI";
import BannerRepository from "../../costo/services/bannerRepository";
import BannerCarousel from "../catalogo/bannerCarousel";
import { listenBanners } from "../services/firebaseClient";
import appState from "../../costo/core/stateManager";

let productosSource = [];
let isStoreOpen = true;
let bannerCarousel = null;

const hidePreloader = () => {
  const preloader = document.querySelector("#preloader");
  if (preloader && !preloader.classList.contains("hidden")) {
    preloader.classList.add("hidden");
  }
};

const filters = {
  search: "",
  category: "all",
  availableOnly: false,
  sort: "todos",
};

const updateResultsStatus = (count, message = "") => {
  const status = document.querySelector("#catalogo-results-status");
  if (!status) return;

  if (message) {
    status.textContent = message;
    return;
  }

  const categoryLabel =
    filters.category === "all" ? "todas las categorias" : `la categoria ${filters.category}`;
  const availabilityLabel = filters.availableOnly ? " Solo disponibles." : "";
  const countLabel = count === 1 ? "1 producto visible." : `${count} productos visibles.`;
  status.textContent = `${countLabel} Mostrando ${categoryLabel}.${availabilityLabel}`;
};

const getCurrentPrice = (producto) => {
  return getPriceForPresentation(producto);
};

const renderProducts = () => {
  const filtered = filterProducts(productosSource, filters);
  const isTodosSort = filters.sort === "todos";
  const isPriceSort = filters.sort === "precio-asc" || filters.sort === "precio-desc";

  const sorted = sortProducts(
    filtered,
    {
      sortKey: filters.sort,
      featuredFirst: isTodosSort,
      pricePriority: isPriceSort,
    },
    getCurrentPrice
  );

  renderCatalog({
    targetSelector: "#catalogo-grid",
    productos: sorted,
    isStoreOpen,
  });

  updateResultsStatus(sorted.length);
};

const renderEmptyOffline = () => {
  const grid = document.querySelector("#catalogo-grid");
  if (!grid) return;

  grid.innerHTML = `
    <div class="catalogo-empty">
      <h3>Catalogo no disponible</h3>
      <p>Conectate para cargar catalogo.</p>
    </div>
  `;
  updateResultsStatus(0, "Catalogo no disponible sin conexion.");
};

const renderBanners = (banners) => {
  const bannerContainer = document.querySelector("#catalogo-banner");
  if (!bannerContainer) return;

  if (bannerCarousel) {
    bannerCarousel.destroy();
  }

  bannerCarousel = new BannerCarousel(bannerContainer, banners);
};

const loadBanners = async () => {
  try {
    if (appState.banners && appState.banners.length > 0) {
      renderBanners(appState.banners);
      return;
    }

    const allBanners = await BannerRepository.findAll();
    const now = new Date();
    const activeBanners = allBanners
      .filter((banner) => banner.active)
      .filter((banner) => {
        if (banner.startDate && banner.startDate > now) return false;
        if (banner.endDate && banner.endDate < now) return false;
        return true;
      })
      .sort((a, b) => a.order - b.order);

    renderBanners(activeBanners);
  } catch (error) {
    console.error("Error cargando banners:", error);
    const bannerContainer = document.querySelector("#catalogo-banner");
    if (bannerContainer) {
      bannerContainer.hidden = true;
    }
  }
};

const formatPrice = (value) =>
  `$${value.toLocaleString("es-AR", { minimumFractionDigits: 0 })}`;

const renderFloatingCart = () => {
  const root = document.querySelector("#floating-cart-root");
  if (!root) return;

  const cart = getCart();
  const count = getItemCount(cart);
  const total = getTotal(cart);

  if (count === 0) {
    root.innerHTML = "";
    return;
  }

  const label = count === 1 ? "1 producto" : `${count} productos`;
  root.innerHTML = `
    <a href="checkout.html" class="floating-cart" aria-label="Ir al checkout: ${label}, total ${formatPrice(total)}">
      <span class="floating-cart__icon" aria-hidden="true">🛒</span>
      <span class="floating-cart__text">${label} - ${formatPrice(total)}</span>
    </a>
  `;
};

const renderCategoryChips = () => {
  const container = document.querySelector("#category-chips");
  if (!container) return;

  const categories = getCategories(productosSource);
  const current = filters.category;

  container.innerHTML = [
    { value: "all", label: "Todas" },
    ...categories.map((cat) => ({ value: cat, label: cat })),
  ]
    .map(
      ({ value, label }) =>
        `<button type="button" class="catalogo-filtros__chip ${
          current === value ? "is-active" : ""
        }" data-category="${value}" aria-pressed="${current === value}">${label}</button>`
    )
    .join("");
};

const setupFilters = () => {
  const searchInput = document.querySelector("#search-input");
  const chipsContainer = document.querySelector("#category-chips");
  const availableOnly = document.querySelector("#available-only");
  const sortSelect = document.querySelector("#sort-select");

  if (!searchInput || !chipsContainer || !availableOnly || !sortSelect) return;

  searchInput.addEventListener("input", (event) => {
    filters.search = event.target.value;
    renderProducts();
  });

  chipsContainer.addEventListener("click", (event) => {
    const chip = event.target.closest(".catalogo-filtros__chip");
    if (!chip) return;

    filters.category = chip.dataset.category;
    chipsContainer.querySelectorAll(".catalogo-filtros__chip").forEach((element) => {
      const isActive = element.dataset.category === filters.category;
      element.classList.toggle("is-active", isActive);
      element.setAttribute("aria-pressed", isActive);
    });
    renderProducts();
  });

  availableOnly.addEventListener("change", (event) => {
    filters.availableOnly = event.target.checked;
    renderProducts();
  });

  sortSelect.addEventListener("change", (event) => {
    filters.sort = event.target.value;
    renderProducts();
  });
};

const setupOfflineBanner = () => {
  const banner = document.createElement("div");
  banner.className = "offline-banner";
  banner.setAttribute("role", "status");
  banner.textContent = "Estas offline";
  banner.hidden = isOnline();

  const headerRoot = document.querySelector("#header-root");
  if (headerRoot) {
    headerRoot.insertAdjacentElement("afterend", banner);
  } else {
    document.body.prepend(banner);
  }

  onOnline(() => {
    banner.hidden = true;
  });

  onOffline(() => {
    banner.hidden = false;
  });
};

const setupCatalogInteractions = (cartUI) => {
  if (!cartUI) return;
  const grid = document.querySelector("#catalogo-grid");
  if (!grid) return;

  grid.addEventListener("click", (event) => {
    // Usar closest: el click suele caer en un hijo (img, span del hint, etc.)
    const actionEl = event.target?.closest?.("[data-action]");
    const action = actionEl?.dataset?.action;
    if (!action) return;

    const card = actionEl.closest(".catalogo-card");

    const id = card?.dataset?.id;
    if (!id) return;

    const producto = productosSource.find((item) => item.id === id);
    if (!producto) return;

    const productQuantity = producto.quantity || producto.cantidad || producto.porciones || 1;
    const productPresentation = producto.presentation || producto.presentacion || "porcion";
    const cantidad = producto.quantity || producto.cantidad || producto.porciones || 1;
    const presentacion = producto.presentation || producto.presentacion || "porcion";
     // const productPresentation = `${cantidad} ${presentacion}`;
    const price = getPriceForPresentation(producto);

    if (action === "add") {
      card?.classList.add("is-adding");
      setTimeout(() => card?.classList.remove("is-adding"), 500);
      addItem({
        id: producto.id,
        name: producto.nombre || producto.name,
        presentation: productPresentation,
        productQuantity,
        productPresentation,
        price,
        imageUrl: producto.imageUrl || producto.imagen || null,
      });
      cartUI.render(getCart());
      renderFloatingCart();
      cartUI.open();
      return;
    }

    if (action === "detail") {
      openProductModal({
        rootSelector: "#modal-root",
        producto,
        presentation: `${cantidad} ${presentacion}`,
        isStoreOpen,
        onAdd: () => {
          addItem({
            id: producto.id,
            name: producto.nombre || producto.name,
            presentation: productPresentation,
            productQuantity,
            productPresentation,
            price,
            imageUrl: producto.imageUrl || producto.imagen || null,
          });
          cartUI.render(getCart());
          renderFloatingCart();
          cartUI.open();
        },
      });
    }
  });
};

const initCatalogo = async () => {
  trackPageView();

  let config = null;
  if (isOnline()) {
    try {
      initFirebase();
      config = await fetchPublicConfig();
    } catch (error) {
      console.warn("Error cargando configuracion en catalogo:", error);
    }
  }

  renderHeader("#header-root", config);
  renderFooter("#footer-root", config);
  mountPublicAuth();
  initCart();
  setupOfflineBanner();

  if (isOnline()) {
    loadBanners();
    listenBanners((banners) => {
      const now = new Date();
      const activeBanners = banners
        .filter((banner) => banner.active)
        .filter((banner) => {
          if (banner.startDate && banner.startDate > now) return false;
          if (banner.endDate && banner.endDate < now) return false;
          return true;
        })
        .sort((a, b) => a.order - b.order);

      renderBanners(activeBanners);
    });
  }

  try {
    window.createScheduleComponent = createScheduleComponent;
  } catch (err) {
    console.warn("No se pudo exponer createScheduleComponent:", err);
  }

  try {
    const scheduleContainer = document.querySelector("#schedule-status");
    if (scheduleContainer) {
      await createScheduleComponent("#schedule-status");
      const schedule = await getSchedule();
      isStoreOpen = isOpenNow(schedule);
      renderProducts();
    }

    setInterval(async () => {
      try {
        const schedule = await getSchedule();
        const open = isOpenNow(schedule);
        if (open !== isStoreOpen) {
          isStoreOpen = open;
          renderProducts();
        }
      } catch (err) {
        console.warn("Error actualizando estado de horario:", err);
      }
    }, 60000);
  } catch (widgetError) {
    console.warn("No se pudo inicializar el widget de horarios:", widgetError);
  }

  const cartUI = createCartUI({
    rootSelector: "#cart-drawer-root",
    toggleSelector: "#cart-toggle",
    countSelector: "#cart-count",
    onRemove: (id, presentation) => {
      removeItem(id, presentation);
      cartUI.render(getCart());
      renderFloatingCart();
    },
    onSetQuantity: (id, presentation, action) => {
      const cart = getCart();
      const item = cart.items.find(
        (current) => current.id === id && current.presentation === presentation
      );

      if (!item) return;

      const nextQuantity = action === "increase" ? item.quantity + 1 : item.quantity - 1;
      setQuantity(id, presentation, nextQuantity);
      cartUI.render(getCart());
      renderFloatingCart();
    },
    onCheckout: () => {
      window.location.href = "checkout.html";
    },
  });

  cartUI?.render(getCart());
  renderFloatingCart();
  setupFilters();
  setupCatalogInteractions(cartUI);

  const cached = getCatalog();
  productosSource = cached;
  renderCategoryChips();

  if (cached.length) {
    renderProducts();
    hidePreloader();
  } else if (!isOnline()) {
    renderEmptyOffline();
    hidePreloader();
  } else {
    updateResultsStatus(0, "Cargando catalogo.");
  }

  if (isOnline()) {
    try {
      const firestoreProducts = await listProducts();
      if (firestoreProducts && firestoreProducts.length > 0) {
        productosSource = firestoreProducts;
        writeCache(firestoreProducts);
        renderCategoryChips();
        renderProducts();
        hidePreloader();
        return;
      }
    } catch (error) {
      console.warn("Error cargando productos desde Firestore:", error);
    }
  }

  refreshCatalog(async () => productosMock).then((data) => {
    if (data.length) {
      productosSource = data;
      renderCategoryChips();
      renderProducts();
      hidePreloader();
      return;
    }

    if (!cached.length && !isOnline()) {
      renderEmptyOffline();
      hidePreloader();
    }
  });
};

document.addEventListener("DOMContentLoaded", initCatalogo);
